import { Component, Input, OnInit } from '@angular/core';
import { MatOptionSelectionChange } from '@angular/material/core';
import { GridApi, GridOptions } from 'ag-grid-community';
import { BareMetricsCalendarPreset } from '../../reusables/baremetrics-calendar/baremetrics-calendar.component';
import { AgGridHelperService } from '../../reusables/services/ag-grid-helper.service';
import { IntegrationLog, Result, SelectedFilters } from './integration-mongo-logs.model';
import { IntegrationMongoLogsService } from './integration-mongo-logs.service';
import { IntegrationMongoLogsCellRendererComponent } from './integration-mongo-logs-cell-renderer/integration-mongo-logs-cell-renderer.component';
import { CVLocalizationService } from '../../reusables/services/cv-localization.service';

@Component({
    selector: 'cv-integration-mongo-logs',
    templateUrl: './integration-mongo-logs.component.html',
    styleUrls: ['./integration-mongo-logs.component.scss']
})
export class IntegrationMongoLogsComponent implements OnInit {

    gridOptions: GridOptions = null;
    logs: IntegrationLog[];
    earliestDate: Date;
    selectedFilters: SelectedFilters = {
        StartDate: new Date(),
        EndDate: new Date(),
        Result: [Result.Failure]
    }
    dateRangePresets: BareMetricsCalendarPreset[];
    dateRangeHeaders: string[] = [];
    selectedPreset: BareMetricsCalendarPreset = null;
    filtersApplied: boolean = false;
    isLoading: boolean = false;
    presetsLoaded: boolean = false;
    resultOptions: string[];
    filterText: string = "";
    failureText: string;
    successText: string;
    failureRowText: string;
    allText: string;
    gridApi: GridApi;
    
    constructor(
        public service: IntegrationMongoLogsService,
        public agGridHelperService: AgGridHelperService,
        public cvLocalizationService: CVLocalizationService,
    ) { }

    async ngOnInit() {
        await this.cvLocalizationService.GetSection("Common");
        await this.cvLocalizationService.GetSection("Controls");
        await this.cvLocalizationService.GetSection("Integration_Logs");

        this.failureText = this.cvLocalizationService.getCachedText("Integration_Logs", "Failure", "Failure");
        this.successText = this.cvLocalizationService.getCachedText("Integration_Logs", "Success", "Success");
        this.failureRowText = this.cvLocalizationService.getCachedText("Integration_Logs", "Contains_Failures", "Contains Failures");
        this.allText = this.cvLocalizationService.getCachedText("Common", "All", "All");
        this.resultOptions = [this.allText, this.failureRowText, this.successText]; 
        this.loadPresets();
        this.setupGrid();
    }

    setupGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            columnDefs: [
                {
                    headerName: "Frequency ID",
                    colId: "FrequencyId",
                    field: "FrequencyId",
                    sortable: true,
                    rowGroup: true,
                    hide: true,
                },
                {
                    headerName: "API Endpoint",
                    colId: "ApiEndpoint",
                    field: "ApiEndpoint",
                    sortable: true,
                    aggFunc: 'first',
                    tooltipField: "ApiEndpoint",
                    tooltipValueGetter: params => params.value,
                },
                {
                    headerName: this.getLocaleText("Result", "Result"),
                    colId: "Result",
                    field: "Successful",
                    sortable: true,
                    aggFunc: params => { return params.values.some(value => value) },
                    valueFormatter: params => this.getValueFormat(params.value, params.node.group),
                    getQuickFilterText: params => this.getValueFormat(params.value, params.node.group),
                    cellDataType: 'text'
                },
                {
                    headerName: this.getLocaleText("Consecutive_Failures", "Consecutive Failures"),
                    colId: "ConsecutiveFailures",
                    field: "ConsecutiveFailures",
                    sortable: true,
                    aggFunc: 'first'
                },
                {
                    headerName: this.getLocaleText("Last_Records_Returned", "Last Records Returned"),
                    colId: "LastRecordsReturned",
                    field: "LastRecordsReturned",
                    sortable: true,
                    aggFunc: 'first'
                },
                {
                    headerName: this.getLocaleText("Last_Attempt","Last Attempt"),
                    colId: "LastAttempt",
                    field: "LastAttempt",
                    sortable: true,
                    aggFunc: 'first',
                    comparator: (a, b) => this.compareDateStrings(a, b),
                },
                {
                    headerName: this.getLocaleText("Last_Run_Date", "Last Run Date"),
                    colId: "LastRunDate",
                    field: "LastRunDate",
                    sortable: true,
                    aggFunc: 'first',
                    comparator: (a, b) => this.compareDateStrings(a, b)
                },
                {
                    headerName: this.getLocaleText("Run_Start_Time", "Run Start Time"),
                    colId: "RunStartTime",
                    field: "RunStartTime",
                    sortable: true,
                    comparator: (a, b) => this.compareDateStrings(a, b)
                },
                {
                    headerName: this.getLocaleText("Run_End_Time", "Run End Time"),
                    colId: "RunEndTime",
                    field: "RunEndTime",
                    sortable: false,
                },
                {
                    headerName: this.getLocaleText("Warnings", "Warnings"),
                    colId: "Warnings",
                    field: "Warnings",
                    sortable: true,
                    cellRenderer: 'IntegrationLogsCellRenderer',
                },
            ],
            autoGroupColumnDef: {
                headerName: this.getLocaleText("Frequency_Id", "Frequency ID"),
                minWidth: 134,
                width: 134,
            },
            defaultColDef: {
                sortable: true,
                filter: true,
                resizable: true,
                suppressMovable: true,
            },
            rowData: this.logs,
            rowSelection: 'single',
            suppressRowClickSelection: true,
            suppressCellFocus: true,
            accentedSort: true,
            animateRows: true,
            alwaysShowVerticalScroll: true,
            getRowId: params => `${params.data.FrequencyId} - ${params.data.RowId}`,
            suppressAggFuncInHeader: true,
            loadingOverlayComponent: 'customLoadingOverlay',
            components: {
                IntegrationLogsCellRenderer: IntegrationMongoLogsCellRendererComponent, 
                customLoadingOverlay: this.agGridHelperService.loadingOverlay,
            },
            onGridReady: (grid) => {
                this.gridApi = grid.api;
                if (this.isLoading)
                    grid.api.showLoadingOverlay();
            },
        });
    }

    loadPresets() {
        const localOffset = new Date().getTimezoneOffset() / 60;
        const today = new Date().addHours(localOffset);
        const todayCopy = new Date().addHours(localOffset);
        const thisWeekStart = today.getDay() == window['CVWeekStart'] ? today : todayCopy.moveToDayOfWeek(window['CVWeekStart'], -1);
        this.earliestDate = today.clone().addDays(-10);

        this.dateRangePresets = [
            {   
                name: "Today",
                label: this.cvLocalizationService.getCachedText("Controls", "Today", "Today"),
                start: today,
                end: today
            },
            {
                name: "Yesterday",
                label: this.cvLocalizationService.getCachedText("Controls", "Yesterday", "Yesterday"),
                start: today.clone().addDays(-1),
                end: today.clone().addDays(-1)
            },
            {   
                name: "This Week",
                label: this.cvLocalizationService.getCachedText("Controls", "This_Week", "This Week"),
                start: thisWeekStart,
                end: thisWeekStart.clone().addDays(6)
            },
            {
                name: "Last Week",
                label: this.cvLocalizationService.getCachedText("Controls", "Last_Week", "Last Week"),
                start: thisWeekStart.clone().addDays(-7),
                end: thisWeekStart.clone().addDays(-1)
            },
        ];

        this.dateRangeHeaders = [
            this.cvLocalizationService.getCachedText("Common", "Days", "Days"),
            this.cvLocalizationService.getCachedText("Common", "Weeks", "Weeks"),
            this.cvLocalizationService.getCachedText("Common", "Months", "Months"),
            this.cvLocalizationService.getCachedText("Common", "Quarters", "Quarters"),
            this.cvLocalizationService.getCachedText("Common", "Years", "Years")
        ];

        this.presetsLoaded = true;
    }

    async loadLogs() {
        try {
            this.isLoading = true;
            this.filtersApplied = true;
            this.gridApi?.showLoadingOverlay();
            this.logs = [];
            this.logs = await this.service.getIntegrationLogs(this.selectedFilters);
        }
        catch {
            //do nothing
        }
        finally {
            this.gridOptions.rowData = this.logs;
            this.gridApi?.setGridOption('rowData', this.logs);
            this.isLoading = false;
            this.gridApi?.hideOverlay();
        }
    }

    selectAll() {
        if (this.selectedFilters.Result.length < 2) {
            this.selectedFilters.Result = []; //[] is all
        } else {
            this.selectNone();
        }
    }

    selectNone () {
        this.selectedFilters.Result = [];
    }

    onMatSelectChange(change: MatOptionSelectionChange) {
        if (!change.isUserInput)
            return;

        const id: number = change.source.value;
        const index = this.selectedFilters.Result.indexOf(id);

        let newValue = [...this.selectedFilters.Result];
        if (id === 0 && newValue.some(v => v === 0))
            newValue = []; //select none
        else if (id === 0)
            newValue = [0]; //select all
        else if (newValue.some(v => v === 0))
            newValue = [id];
        else if (index >= 0)
            newValue.splice(index, 1);
        else
            newValue.push(id);

        this.selectedFilters.Result = newValue;
    }

    compareDateStrings(a: string, b: string): number {
        const dateA = new Date(a);
        const dateB = new Date(b);
        if (dateA === dateB)
            return 0;
        if (dateA > dateB)
            return 1;
        if (dateA < dateB)
            return -1;
    }

    onTextChanged() {
        this.gridApi?.setGridOption('quickFilterText', this.filterText);
    }

    getLocaleText(key: string, defaultValue?: string) {
        return this.cvLocalizationService.getCachedText("Integration_Logs", key, defaultValue);
    }

    getValueFormat(value: boolean, group: boolean) {
        if (group) {
            return value ? this.successText : this.failureRowText;
        }
        return value ? this.successText : this.failureText;
    }

    clearFilter() {
        this.filterText = "";
        this.gridApi?.setGridOption('quickFilterText', this.filterText);
    }
}
